import React, { useState, useRef, useEffect } from "react";

import { createUseStyles } from "react-jss";
import ReactAvatarEditor from "react-avatar-editor";
import { LoadingOutlined } from "@ant-design/icons";
import { faMinus, faUser } from "@fortawesome/free-solid-svg-icons";
import { faCamera } from "@fortawesome/pro-regular-svg-icons";
import { Upload, Avatar, Slider, Modal, Divider, App } from "antd";

import Icon from "../../Icon";
import UIText from "./UIText";
import UIButton from "./UIButton";
import UIHeading from "./UIHeading";

const antIcon = (<LoadingOutlined style={{ fontSize: 20, color: "white" }} spin />);

const AvatarUploader = ({
  onClick,
  backgroundColor = "#dae3ed",
  iconColor = "white",
  loading,
  uri,
  style,
  removeProfile
}) => {
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [visible, setVisible] = useState(false);
  const [newImage, setNewImage] = useState();
  const [editorSize, setEditorSize] = useState(300);
  const editor = useRef(null);
  const { message } = App.useApp();
  const styles = useStyle({ style, backgroundColor });

  useEffect(() => {
    if (!loading) {
      setVisible(false);
    }
    
      const handleResize = () => {
        const windowWidth = window.innerWidth;
        if (windowWidth < 600) {
          setEditorSize(80);
        } else {
          setEditorSize(150);
        }
      };
      
      handleResize();
      window.addEventListener('resize', handleResize);
    
      return () => {
        window.removeEventListener('resize', handleResize);
        };
      }, [loading]);

  const onUploadClick = (e) => {
    let url = window?.URL?.createObjectURL(e?.file?.originFileObj);
    setNewImage(url);
    setVisible(true);
  };

  function dataURItoFile(dataURI) {
    var byteString = atob(dataURI.split(",")[1]);
    var mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
    var ab = new ArrayBuffer(byteString.length);
    var ia = new Uint8Array(ab);
    for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new File([ab], new Date().getTime() + ".png", { type: mimeString });
  }

  const onSave = () => {
    let dataUrl = editor.current?.getImage()?.toDataURL();
    if (dataUrl) {
      onClick({
        fileList: [{ originFileObj: dataURItoFile(dataUrl) }],
      });
    } else {
      message.error("Something went wrong");
    }
  };
  const removePictureModal = () => {
    Modal.confirm({
      title: 'Do you want to remove profile picture?',
      centered: true,
      okText: 'Yes',
      okType: 'primary',
      cancelText: 'No',
      onOk() {
        removeProfile()
      },
    });
  }
  let modalBody = {
    background: "#EEE",
    borderRadius: 7,
  };

  const onCancel = () => {
    setVisible(false);
    setNewImage(uri);
  };
  const onZoom = (e) => {
    setZoom(e);
  };
  const onRotate = (e) => {
    setRotate(e);
  };
  return (
    <>
      <div className={styles.main}>
        {
          uri && removeProfile && (
            <span className="ui-avatar-remove-icon" onClick={removePictureModal}>
              <Icon color="black" size={15} Icon={faMinus} />
            </span>
          )
        }
        <div className={styles.iconBtn}>
          <Upload
            className={styles.upload}
            onChange={onUploadClick}
            maxCount={1}
            showUploadList={false}
          >
            <Icon color="white" size={22} Icon={faCamera} />
          </Upload>
        </div>
        {uri ? (
          <div className={styles.avatarDiv}>
            <Avatar className={styles.avatar} size={editorSize} src={uri} />
          </div>
        ) : (
          <Icon color={iconColor} size={80} Icon={faUser} />
        )}
      </div>
      <Modal
        footer={null}
        mask={{ closable: false }}
        open={visible}
        styles={{ body: modalBody }}
        onCancel={onCancel}
      >
        <UIHeading level={3}>Profile photo</UIHeading>
        <div className={styles.modalBody}>
          <Divider />
          <ReactAvatarEditor
            ref={editor}
            scale={parseFloat(zoom)}
            width={editorSize}
            height={editorSize}
            rotate={parseFloat(rotate)}
            borderRadius={editorSize / 2}
            image={newImage}
          />
          <Divider />

          <div className={styles.zoomDiv}>
            <div className={styles.modalTitle}>
              <UIText>Zoom</UIText>
            </div>
            <div className={styles.modalSlider}>
              <Slider
                min={1}
                max={3}
                value={zoom}
                onChange={onZoom}
                step={0.1}
                className={styles.zoomSlider}
              />
              <UIText style={{ width: 50, textAlign: "center" }}>{zoom}</UIText>
            </div>
          </div>
          <div className={styles.straightenDiv}>
            <div className={styles.modalTitle}>
              <UIText>Straighten</UIText>
            </div>
            <div className={styles.modalSlider}>
              <Slider
                min={-30}
                max={30}
                value={rotate}
                onChange={onRotate}
                step={1}
                className={styles.straightenSlider}
              />
              <UIText style={{ width: 50, textAlign: "center" }}>{rotate}</UIText>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <Upload
              onChange={onUploadClick}
              maxCount={1}
              showUploadList={false}
            >
              <UIButton type="secondary">Change photo</UIButton>
            </Upload>
            <UIButton loading={loading} onClick={onSave}>
              Save photo
            </UIButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

const useStyle = createUseStyles({
  main: (props) => ({
    ...props.style,
    height: 130,
    width: 130,
    borderRadius: 80,
    background: props.backgroundColor,
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    position: "relative",
    "&:hover": {
      "& div": {
        display: "flex",
      },
    },
    "@media (max-width: 600px)": {
      height: 80,
      width: 80,
      borderRadius: 40,
    },
  }),
  avatar: {
    backgroundColor: "white",
    "@media (max-width: 600px)": {
      height: 80,
      width: 80,
    },
  },
  upload: {
    cursor: "pointer",
  },
  iconBtn: {
    height: "50%",
    background: "#00000050",
    position: "absolute",
    zIndex: 1,
    width: "100%",
    bottom: 0,
    borderBottomRightRadius: 80,
    borderBottomLeftRadius: 80,
    display: "none",
    alignItems: "center",
    justifyContent: "center",
  },
  modalBody: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
  zoomDiv: {
    display: "flex",
    width: "100%",
    flexDirection: 'column',
    alignItems: "center",
    justifyContent: "center",
  },
  zoomSlider: {
    width: "100%",
    marginLeft: 20,
    marginRight: 10,
  },
  straightenDiv: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    flexDirection: 'column',
    justifyContent: "center",
    marginBottom: 10,
  },
  straightenSlider: {
    width: "100%",
    marginLeft: 20,
  },
  modalFooter: {
    alignItems: "center",
    justifyContent: "space-around",
    display: "flex",
    width: "80%",
    marginTop: 20,
  },
  modalSlider: {
    display: 'flex',
    width: '100%'
  },
  modalTitle: {
    width: '100%',
    paddingLeft: 14
  }
});

export default AvatarUploader;
